import React, { memo, useState } from 'react'
import cx from 'classnames'
import { useIntl } from 'intl'
import { getAnalyticsContext, track } from 'analytics'
import { processError } from 'helpers'
import { useCartDelete } from 'modules/cart'
import type { CartDeleteInput } from 'modules/cart'

import { Button, buttonMessages } from 'components/inputs'
import { Icon, Text } from 'components/dataDisplay'

import messages from './messages'


export const bgColorToClassName = {
  'gray-20': 'bg-gray-20',
  'light-beige': 'bg-light-beige',
} as const

export type BgColor = keyof typeof bgColorToClassName

type OrderItemRemoveButtonProps = {
  bgColor?: BgColor
  input: CartDeleteInput
  metadata: Analytics.Events['Cart product remove']
}

const OrderItemRemoveButton: React.FunctionComponent<OrderItemRemoveButtonProps> = (props) => {
  const { bgColor = 'light-beige', input, metadata } = props

  const intl = useIntl()

  const [ isOpen, setOpen ] = useState(false)
  const [ deleteMutation, { isFetching } ] = useCartDelete()

  const handleCancel = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleConfirm = async () => {
    try {
      const { page, pageType } = getAnalyticsContext()

      await deleteMutation({
        ...input,
        metadata: {
          page,
          pageType,
          ...input?.metadata,
        },
      })

      track('Cart product remove', metadata)
    }
    catch (error) {
      processError(error)
    }
  }

  return (
    <>
      <button
        type="button"
        className="absolute right-0 top-0 mr-8 mt-8 cursor-pointer"
        title={intl.formatMessage(buttonMessages.remove)}
        onClick={handleOpen}
        data-testid="removeOrderItem"
      >
        <Icon name="16/close" />
      </button>
      {
        isOpen && (
          <div className={cx('absolute inset-0 z-overlay rounded', bgColorToClassName[bgColor])}>
            <div
              className="align-center absolute inset-0 flex max-h-[254rem] flex-col justify-center text-center"
              aria-busy={isFetching}
            >
              <Text style="p3" message={messages.confirmation.text} />
              <div className="mt-24">
                <Button
                  size={38}
                  style="secondary"
                  title={messages.confirmation.no}
                  disabled={isFetching}
                  onClick={handleCancel}
                  data-testid="removeOrderItemCancel"
                />
                <Button
                  className="ml-12"
                  size={38}
                  style="primary"
                  title={messages.confirmation.yes}
                  disabled={isFetching}
                  onClick={handleConfirm}
                  data-testid="removeOrderItemConfirm"
                />
              </div>
            </div>
          </div>
        )
      }
    </>
  )
}


export default memo(OrderItemRemoveButton)
